//@ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Button } from "../ui/Button";
import { ArrowUp, X, BookOpen, Inbox } from "lucide-react";
import {
  addUserMessage,
  clearChat,
  fetchChatHistory,
  setContextTitle,
  streamBotResponse,
} from "../../features/chat/chatSlice";
import { setChatbotOpen } from "../../features/ui/uiSlice";
import {
  Conversation,
  ConversationContent,
  Message as AiElementMessage,
  MessageContent,
} from "../ai-elements";
import { Button as BButton } from "../shadcn-ui/b-button";
import { SparkleIconComponent } from "../icons/Icons";
import { useNavigate } from "react-router-dom";
const MOCK_COURSES = [
  { id: "1", name: "Next.js 15 Deep Dive" },
  { id: "2", name: "Redux Toolkit State Mastery" },
  { id: "3", name: "Tailwind CSS Responsive Architecture" },
  { id: "4", name: "AI Web Automation Essentials" },
];

function AiWidgetsComponent({ page }: { page: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [chatInput, setChatInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.ui.isChatbotOpen);
  const messages = useAppSelector((state) => state.chat.messages);
  const history = useAppSelector((state) => state.chat.history);
  const historyLoading = useAppSelector((state) => state.chat.historyLoading);
  const historyPage = useAppSelector((state) => state.chat.historyPage);
  const historyHasMore = useAppSelector((state) => state.chat.historyHasMore);
  const isLoading = useAppSelector((state) => state.chat.isLoading);
  const streamStatus = useAppSelector((state) => state.chat.streamStatus);
  const contextTitle = useAppSelector((state) => state.chat.contextTitle);
  const prevIsLoadingRef = useRef(isLoading);
  const [lastScrollHeight, setLastScrollHeight] = useState(0);
  const [clickedHistoryLoad, setClickedHistoryLoad] = useState(false);
  const { baseId, jobId, applicationId } = useParams<{
    baseId: string;
    jobId: string;
    applicationId: string;
  }>();

  const topMessageIdRef = useRef<string | null>(null);
  const isFetchingHistoryRef = useRef(false);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState(MOCK_COURSES);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);

  const segments = location.pathname.split("/").filter(Boolean);
  const routeSegment = segments[1] || segments[0] || "";
  const selectedPrefix = contextTitle ? `${routeSegment}: ${contextTitle}` : "";
  const normalizedHistory = history;

  const allVisibleMessages = [...normalizedHistory, ...messages];
  const isChatEmpty = allVisibleMessages.length === 0;
  const showInitialHistoryPrompt = historyPage === 0 && history.length === 0;

  const showMoreHistoryButton =
    historyHasMore && (historyPage > 0 || allVisibleMessages.length > 0);
  const isHistoryEmpty =
    clickedHistoryLoad && history.length === 0 && !historyLoading;

  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading]);

  const prevMessageCount = useRef(0);

  const ctxKey = page
    ? `${page}`
    : applicationId
      ? `candidate:${applicationId}`
      : jobId
        ? `job:${jobId}`
        : "global";
  const prevCtxKeyRef = useRef<string | null>(ctxKey);

  useEffect(() => {
    if (prevCtxKeyRef.current !== ctxKey) {
      prevCtxKeyRef.current = ctxKey;
      setClickedHistoryLoad(false);
      dispatch(clearChat());
      dispatch(setContextTitle(null));
    }
  }, [ctxKey, dispatch]);

  useEffect(() => {
    if (!scrollRef.current) return;

    if (allVisibleMessages.length > prevMessageCount.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight - lastScrollHeight,
        behavior: "smooth",
      });
      setLastScrollHeight(scrollRef.current.scrollHeight);
    }

    prevMessageCount.current = allVisibleMessages.length;
  }, [allVisibleMessages]);

  const handleSendMessage = async (
    e?: React.FormEvent | React.KeyboardEvent,
  ) => {
    if (e) e.preventDefault();
    if ((!chatInput.trim() && selectedCourses.length === 0) || isLoading)
      return;
    setLastScrollHeight(0);
    const tagsPrefix = selectedCourses
      .map((course) => `[Context Course: ${course}]`)
      .join(" ");
    const userText = `${tagsPrefix} ${chatInput.trim()}`.trim();

    setChatInput("");
    setSelectedCourses([]);

    isFetchingHistoryRef.current = false;
    dispatch(addUserMessage(userText));

    const entityId = applicationId ? applicationId : jobId;
    const ongoingPayload: any = {
      role: "user",
      message: userText,
      entityType: applicationId ? "candidate" : "job",
      entityId,
    };

    dispatch(streamBotResponse(ongoingPayload) as any);
  };

  const handleLoadPrevious = () => {
    if (allVisibleMessages.length > 0) {
      topMessageIdRef.current = allVisibleMessages[0].id;
      isFetchingHistoryRef.current = true;
    }

    setClickedHistoryLoad(true);

    if (historyLoading) return;

    const nextPage = historyPage === 0 ? 1 : historyPage + 1;
    dispatch(
      fetchChatHistory({
        limit: 5,
        page: nextPage,
        entityId: applicationId || jobId || "",
        entityType: applicationId ? "candidate" : "job",
      }) as any,
    )
      .unwrap()
      .catch(() => {
        isFetchingHistoryRef.current = false;
        topMessageIdRef.current = null;
      });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setChatInput(text);

    const match = text.match(/@c(\w*)$/i);
    if (match) {
      const query = match[1].toLowerCase();
      const filtered = MOCK_COURSES.filter((c) =>
        c.name.toLowerCase().includes(query),
      );
      setFilteredCourses(filtered.length > 0 ? filtered : MOCK_COURSES);
      setActiveIndex(0);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectCourseOption = (courseName: string) => {
    if (!textareaRef.current) return;
    if (!selectedCourses.includes(courseName)) {
      setSelectedCourses((prev) => [...prev, courseName]);
    }
    const text = chatInput;
    const selectionStart = textareaRef.current.selectionStart;
    const textBeforeCursor = text.slice(0, selectionStart);
    const textAfterCursor = text.slice(selectionStart);
    const cleanedBeforeCursor = textBeforeCursor.replace(/@c(\w*)$/i, "");

    setChatInput(cleanedBeforeCursor + textAfterCursor);
    setShowSuggestions(false);

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 10);
  };

  const removeCourseTag = (courseName: string) => {
    setSelectedCourses((prev) => prev.filter((name) => name !== courseName));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % filteredCourses.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex(
          (prev) =>
            (prev - 1 + filteredCourses.length) % filteredCourses.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (filteredCourses[activeIndex]) {
          selectCourseOption(filteredCourses[activeIndex].name);
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      }
    }

    if (
      e.key === "Backspace" &&
      chatInput === "" &&
      selectedCourses.length > 0
    ) {
      e.preventDefault();
      setSelectedCourses((prev) => prev.slice(0, -1));
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Add this new function above your handleSendMessage function
  const handleHtmlClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // Find the closest anchor tag in case they clicked on an element inside the <a>
    const anchor = target.closest("a");

    if (anchor) {
      e.preventDefault();
      e.stopPropagation();

      const href = anchor.getAttribute("href");
      if (!href) return;

      try {
        const url = new URL(anchor.href);
        // Check if the link belongs to your application's domain
        if (url.origin === window.location.origin) {
          // Navigate using React Router (preserves SPA behavior)
          navigate(url.pathname + url.search + url.hash);
        } else {
          navigate(url.pathname + url.search + url.hash);
          // It's an external link, open safely in a new tab
          // window.open(anchor.href, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        // Fallback for relative paths like "/dashboard"
        // if (href.startsWith('/')) {
        //   navigate(href);
        // } else {
        //   window.open(href, '_blank', 'noopener,noreferrer');
        // }
      }
    }
  };

  return (
    <>
      <div
        className={`bg-[#ffffff] mt-11 fixed top-2 right-2 bottom-2 flex flex-col overflow-hidden rounded-lg border  transition-all duration-300 z-8 ${
          isOpen
            ? "w-[calc(100vw-1rem)] sm:w-[400px] sm:min-w-[500px] max-w-full opacity-100 pt-2"
            : "w-0 min-w-0 opacity-0 pointer-events-none p-0 border-0"
        }`}
      >
        <div className="flex items-center flex-col w-full pb-2 bg-white shrink-0 relative">
          <div className="flex justify-between w-full items-center gap-4 text-gray-500 px-2">
            <h3 className="font-semibold truncate text-[#09090b] flex items-center gap-2 pl-2">
              <SparkleIconComponent className="w-4 h-4 text-[#808080] " />
              AI Assistant
            </h3>
            <Button
              variant="ghost"
              buttonSize="icon"
              className="h-8 w-8 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer"
              onClick={() => dispatch(setChatbotOpen(false))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="absolute left-0 right-0 bottom-0 border-b -mx-2" />
        </div>
        {/* bg-[#fafafa] */}
        <div className="flex-1 flex flex-col overflow-hidden mt-2">
          <Conversation
            ref={scrollRef}
            className="flex-1 bg-transparent h-full"
          >
            {isChatEmpty ? (
              <div className="flex flex-col items-center justify-center h-full w-full text-center p-6 my-auto space-y-4">
                {showInitialHistoryPrompt && (
                  <div className="space-y-3 flex flex-col items-center">
                    <img
                      src="https://revscale-academy.nyc3.cdn.digitaloceanspaces.com/public/assets/rs.gif"
                      alt="Welcome"
                      className="mx-auto h-24 w-24 object-contain rounded-full"
                    />
                    <p className="text-sm font-medium text-gray-600 px-6">
                      Hi there! 👋 How can I help you navigate our platform
                      today?
                    </p>
                  </div>
                )}

                {isHistoryEmpty ? (
                  <div className="text-xs text-gray-400 font-medium flex items-center gap-1.5 bg-gray-100/70 px-3 py-1.5 rounded-full animate-in fade-in duration-200">
                    <Inbox className="h-3.5 w-3.5 text-gray-400" />
                    No older messages found
                  </div>
                ) : (
                  historyHasMore && (
                    <Button
                      variant="secondary"
                      buttonSize="small"
                      onClick={handleLoadPrevious}
                      disabled={historyLoading}
                      className="rounded-full px-4 py-2 text-[11px] tracking-[0.1em] font-medium transition-colors"
                    >
                      {historyLoading ? "Loading..." : "Previous messages"}
                    </Button>
                  )
                )}
              </div>
            ) : (
              <ConversationContent className="p-4 space-y-4">
                {showMoreHistoryButton && (
                  <div className="flex justify-center pb-2">
                    <Button
                      variant="secondary"
                      buttonSize="small"
                      onClick={handleLoadPrevious}
                      disabled={historyLoading}
                      className="rounded-full px-3 py-1.5 text-[11px] tracking-[0.08em]"
                    >
                      {historyLoading ? "Loading..." : "Previous messages"}
                    </Button>
                  </div>
                )}

                {isHistoryEmpty && (
                  <div className="text-center text-[10px] text-gray-400 font-medium pb-2 tracking-wide uppercase">
                    — Beginning of Chat History —
                  </div>
                )}

                {allVisibleMessages.map((msg) => {
                  const isEmptyStreamingAssistant =
                    isLoading &&
                    msg.role === "assistant" &&
                    !msg.content.trim();

                  if (isEmptyStreamingAssistant) return null;

                  return (
                    <div key={msg.id} data-message-id={msg.id}>
                      <AiElementMessage from={msg.role}>
                        <div
                          onClickCapture={handleHtmlClick}
                          className={`rounded-[20px]  ${
                            msg.role === "user"
                              ? "bg-indigo-600 text-white border-transparent ml-4 py-2 px-3"
                              : " text-gray-800  bg-[#f5f5f5] mr-4 [&_p]:my-1 [&_strong]:font-semibold [&_a]:text-blue-600 [&_a]:underline py-3 px-4"
                          }`}
                        >
                          {msg.role === "assistant" &&
                          /<[a-z][\s\S]*>/i.test(msg.content) ? (
                            <MessageContent>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: msg.content,
                                }}
                              />
                            </MessageContent>
                          ) : (
                            <MessageContent>{msg.content}</MessageContent>
                          )}
                        </div>
                      </AiElementMessage>
                    </div>
                  );
                })}

                {isLoading && (
                  <div className="mr-auto max-w-[85%] flex items-center gap-2 px-3 py-2 text-xs text-gray-500 bg-white border border-gray-200/60 rounded-xl animate-in fade-in slide-in-from-bottom-1 duration-200">
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" />
                    </span>
                    <span className="font-medium">
                      {streamStatus || "Thinking..."}
                    </span>
                  </div>
                )}
              </ConversationContent>
            )}
          </Conversation>
          {/* bg-[#f0f4f9] border-t border-gray-200/60  p-3 */}
          <div className="px-4 py-2 shrink-0 relative flex flex-col gap-2 rounded-[16px]">
            {showSuggestions && (
              <div className="absolute bottom-full left-3 right-3 bg-white border border-gray-200 rounded-xl max-h-[180px] overflow-y-auto mb-2 z-50">
                <div className="p-2 text-[10px] font-bold tracking-wider text-gray-400 border-b border-gray-100 uppercase bg-gray-50/50">
                  Select a Course
                </div>
                <ul className="p-1">
                  {filteredCourses.map((course, idx) => (
                    <li
                      key={course.id}
                      onClick={() => selectCourseOption(course.name)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`flex items-center gap-2 p-2 rounded-lg text-xs cursor-pointer transition-colors ${
                        idx === activeIndex
                          ? "bg-black text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <BookOpen
                        className={`h-3.5 w-3.5 ${idx === activeIndex ? "text-white" : "text-gray-400"}`}
                      />
                      <span className="truncate font-medium">
                        {course.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-sky-100 rounded-2xl capitalize">
              {selectedPrefix && (
                <div className="flex flex-col gap-1.5 px-1 mt-1 pb-1 max-h-[80px] overflow-y-auto">
                  <div className="flex items-center justify-between pl-2 pr-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[12px] font-medium text-gray-700 truncate max-w-[200px] sm:max-w-[250px]">
                        {selectedPrefix}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => dispatch(setContextTitle(null))}
                      className="text-gray-400 hover:text-gray-700 rounded-full p-0.5 transition-colors cursor-pointer hover:bg-gray-100"
                    >
                      <X className="h-3 w-3 stroke-[2.5]" />
                    </button>
                  </div>
                </div>
              )}

              <form
                onSubmit={handleSendMessage}
                onClick={() => textareaRef.current?.focus()}
                className="w-full relative border border-gray-200 rounded-[16px] p-3 flex flex-col bg-white gap-1 transition-shadow focus-within:ring-1 focus-within:ring-indigo-600 focus-within:border-indigo-600"
              >
                <textarea
                  ref={textareaRef}
                  value={chatInput}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Message..."
                  rows={1}
                  className="  w-full resize-none outline-none text-[14px] placeholder:text-gray-400 bg-transparent min-h-[28px] border-0 hover:ring-0 focus:ring-0 px-1 py-2 leading-relaxed cursor-text"
                  disabled={isLoading}
                />

                <div className="flex items-center justify-end w-full mt-1">
                  <BButton
                    type="submit"
                    size="icon-sm"
                    variant="default"
                    loading={isLoading}
                    aria-label={
                      isLoading ? "Streaming response" : "Send message"
                    }
                    disabled={
                      isLoading ||
                      (!chatInput.trim() && selectedCourses.length === 0)
                    }
                    className={`h-7 w-7 rounded-full flex items-center justify-center transition-all ${
                      (chatInput.trim() || selectedCourses.length > 0) &&
                      !isLoading
                        ? "bg-indigo-600 text-white hover:opacity-90 cursor-pointer"
                        : "bg-[#e9eaea] text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <ArrowUp className="h-3.5 w-3.5 stroke-[3]" />
                  </BButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AiWidgetsComponent;
export { AiWidgetsComponent };
